import { DOCUMENT } from '@angular/common';
import { DestroyRef, inject } from '@angular/core';

/** Firefox reports wheel steps in lines (deltaMode 1); about this many px each. */
const LINE_HEIGHT_PX = 16;

/** The sideways-scrolling box around a table: PrimeNG's table container, or
 *  any wrapper a screen gives a wide table. Only boxes holding a table count,
 *  so scrollable tab strips and chip rows keep the browser's own wheel. */
function sidewaysScroller(from: Element | null): HTMLElement | null {
  for (let el = from as HTMLElement | null; el; el = el.parentElement) {
    if (el.scrollWidth > el.clientWidth && el.querySelector('table')) {
      const overflowX = getComputedStyle(el).overflowX;
      if (overflowX === 'auto' || overflowX === 'scroll') return el;
    }
  }
  return null;
}

/**
 * The table a wheel turn scrolls sideways, or null to leave the turn to the
 * browser. Anywhere over a wide table the wheel scrolls it sideways first; at
 * its right end (wheel down) or left end (wheel up) the turn goes back to the
 * browser, which scrolls the rows or the page. So the admin is never stuck in
 * a table, only a few turns away from the page.
 */
export function sidewaysWheelTarget(event: WheelEvent): HTMLElement | null {
  // Shift+wheel and trackpad swipes already scroll sideways in the browser.
  if (event.shiftKey || event.ctrlKey || Math.abs(event.deltaX) >= Math.abs(event.deltaY)) {
    return null;
  }
  const scroller = sidewaysScroller(event.target as Element);
  if (!scroller) return null;
  // At either end the wheel goes back to the page instead of doing nothing.
  const atStart = scroller.scrollLeft <= 0;
  const atEnd = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 1;
  if ((event.deltaY < 0 && atStart) || (event.deltaY > 0 && atEnd)) return null;
  return scroller;
}

/**
 * One listener for every admin table that is wider than its card: the mouse
 * wheel over it scrolls it sideways. Installed once at startup, so new
 * screens get it without doing anything.
 */
export function installTableWheelScroll(): void {
  const doc = inject(DOCUMENT);
  const onWheel = (event: WheelEvent): void => {
    const scroller = sidewaysWheelTarget(event);
    if (!scroller) return;
    scroller.scrollLeft += event.deltaMode === 1 ? event.deltaY * LINE_HEIGHT_PX : event.deltaY;
    event.preventDefault();
  };
  // Not passive: a sideways scroll must stop the page scrolling as well.
  doc.addEventListener('wheel', onWheel, { passive: false });
  inject(DestroyRef).onDestroy(() => doc.removeEventListener('wheel', onWheel));
}
